import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseComponent } from '@wms/components';
import { WmsFeatureDirective } from '@wms/directives';
import { FormsModule } from '@angular/forms';
import { AppLocaleManagerService } from '@wms/i18n';
import { get, includes, isEmpty } from 'lodash-es';
import { FilterPipe } from '@wm/components-base';

@Component({
  selector: 'wms-locale-messages-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    WmsFeatureDirective,
    FilterPipe
  ],
  templateUrl: './locale-messages-tab.component.html',
  styleUrls: ['./locale-messages-tab.component.less']
})
export class LocaleMessagesTabComponent extends BaseComponent implements OnInit {

  @Input() filteredMessagesList: any = [];
  @Input() filteredPrefabMessagesList: any = [];
  @Input() selectedPrefab: string = '';
  @Output() onTempLocaleChange: EventEmitter<any> = new EventEmitter();

  constructor(public appLocaleService: AppLocaleManagerService) {
    super();
  }

  ngOnInit(): void {
  }

  tempLocaleChanged(){
    this.onTempLocaleChange.emit();
  }

  //validate the last selected message, if the model has any missing value
  validateLastSelectedMsg() {
    const oldMsgKey = this.appLocaleService.activeMessage.message;
    const isMessageKeyAbsent = (messages: any, locale: any, prefab?: any) => {
      if (prefab) {
        return !messages[locale][prefab][oldMsgKey];
      } else {
        return !messages[locale][oldMsgKey];
      }
    };
    if (this.selectedPrefab) {
      // If selected prefab, check userPrefabMessages in English and the selected locale
      return isMessageKeyAbsent(this.appLocaleService.userPrefabMessages, 'en', this.selectedPrefab) ||
        (this.appLocaleService.multipleLocaleExists && isMessageKeyAbsent(this.appLocaleService.userPrefabMessages, this.appLocaleService.selectedTempLocale.key, this.selectedPrefab));
    } else {
      // If no selected prefab, check userMessages in English and the selected locale
      return isMessageKeyAbsent(this.appLocaleService.userMessages, 'en') ||
        (this.appLocaleService.multipleLocaleExists && isMessageKeyAbsent(this.appLocaleService.userMessages, this.appLocaleService.selectedTempLocale.key));
    }
  }

  //revert the values for active message to old model
  revertToOldModel() {
    const messageKey = this.appLocaleService.activeMessage.message;
    const oldValues = this.appLocaleService.activeMessage.oldValues;
    // Determine whether to use userPrefabMessages or userMessages based on selectedPrefab
    const messageContainer = this.selectedPrefab ? this.appLocaleService.userPrefabMessages : this.appLocaleService.userMessages;
    const prefabKey = this.selectedPrefab ? this.selectedPrefab : null;
    // Function to revert message for a given locale
    const revertMessage = (locale: any) => {
        const currentMessages = prefabKey ? messageContainer[locale][prefabKey] : messageContainer[locale];
        if (!currentMessages[messageKey]) {
            currentMessages[messageKey] = oldValues[locale];
        }
    };
    // Revert messages for English locale
    revertMessage('en');
    // Revert messages for the secondary locale if it exists
    if (this.appLocaleService.multipleLocaleExists) {
        revertMessage(this.appLocaleService.selectedTempLocale.key);
    }
  }

  showNewField() {
    this.appLocaleService.toggleMessage(false);
    this.appLocaleService.showNewMsgAction = true
    this.appLocaleService.showLocaleAction = true
    this.appLocaleService.addInProgress = true
    this.appLocaleService.activeMessage = {};
    this.appLocaleService.activeFormat = {};
  }

  edit($evt: any, message?: any) {
    this.appLocaleService.showLocaleAction = true;
    let hasError = false,
      secondaryLocale;
    $evt.stopPropagation();
    //do not allow editing existing messages if already editing new message is in progress
    if (this.appLocaleService.addInProgress) {
      this.appLocaleService.toggleMessage(true, this.locale.MESSAGE_APP_MESSAGES_DIALOG_ADD_UNSTABLE_ERROR, 'error');
      return;
    }
    this.appLocaleService.toggleMessage(false);
    if (!isEmpty(this.appLocaleService.activeMessage)) { //if the last selected locale exists then validate it for empty values.
      hasError = this.validateLastSelectedMsg();
      if (hasError) {
        this.revertToOldModel();
        this.appLocaleService.toggleMessage(true, this.locale.LABEL_APP_DEFAULT_MESSAGE_VALUE + ' for ' + this.appLocaleService.activeMessage.message + ' : ' + this.locale.LABEL_APP_MESSAGE_ERROR_EMPTY, 'info');
        this.appLocaleService.activeMessage = {};
        return;
      }
    }
    this.updateActiveMessage(message, secondaryLocale);
  }

  updateActiveMessage(message: any, secondaryLocale: any) {
    let oldValue: any;
    this.appLocaleService.activeMessage = {}; //empty the active message when edit is triggered and then assign respective message to true
    if (includes(this.appLocaleService.deleteList, message) || !message) {
      return;
    }
    this.appLocaleService.activeMessage.message = message;
    this.appLocaleService.activeMessage.oldValues = {};
    if (message) { //when clicked on message key
      this.appLocaleService.activeMessage.en = {};
      this.appLocaleService.activeMessage.en[message] = true;
      oldValue = this.selectedPrefab ? this.appLocaleService.userPrefabMessages.en[this.selectedPrefab][message] : (this.appLocaleService.userMessages.en[message] || this.appLocaleService.systemMessages.en[message]);
      this.appLocaleService.activeMessage.oldValues.en = oldValue;
      if (this.appLocaleService.multipleLocaleExists) {
        secondaryLocale = this.appLocaleService.selectedTempLocale.key;
        oldValue = this.selectedPrefab ? this.appLocaleService.userPrefabMessages[secondaryLocale][this.selectedPrefab][message] : (this.appLocaleService.userMessages[secondaryLocale][message] || this.appLocaleService.systemMessages[secondaryLocale][message]);
        this.appLocaleService.activeMessage.oldValues[secondaryLocale] = oldValue;
        this.appLocaleService.activeMessage[secondaryLocale] = {};
        this.appLocaleService.activeMessage[secondaryLocale][message] = true;
      }
    }
  }

  //toggles the delete state based on the current state
  toggleDelete($event: any, message: any) {
    $event.stopPropagation();
    this.appLocaleService.showLocaleAction = true;
    let messageIndex = this.appLocaleService.deleteList.indexOf(message);
    if (messageIndex > -1) {
      this.appLocaleService.deleteList.splice(messageIndex, 1);
      return;
    }
    if (get(this.appLocaleService.activeMessage, 'en.' + message) || get(this.appLocaleService.activeMessage, this.appLocaleService.selectedTempLocale && this.appLocaleService.selectedTempLocale.key + '.' + message)) {
      this.appLocaleService.activeMessage = {};
    }
    this.appLocaleService.deleteList.push(message);
  }

}
